import { db } from "./schema";

/**
 * Storage-quota awareness for the IndexedDB layer. Uses the StorageManager API
 * for the browser-reported estimate and walks the Dexie tables for a
 * per-category breakdown (IndexedDB does not expose per-store byte counts).
 */

/** Fill-level thresholds (fraction of quota) driving UI warnings. */
export const QUOTA_THRESHOLDS = {
  /** Informational notice. */
  info: 0.5,
  /** Warning. */
  warning: 0.75,
  /** Critical — prompt the user to free space. */
  critical: 0.9,
} as const;

export type WarningLevel = "ok" | "info" | "warning" | "critical";

/** Map a fill fraction (0–1) to a warning level. */
export function warningLevelFor(percent: number): WarningLevel {
  if (percent >= QUOTA_THRESHOLDS.critical) return "critical";
  if (percent >= QUOTA_THRESHOLDS.warning) return "warning";
  if (percent >= QUOTA_THRESHOLDS.info) return "info";
  return "ok";
}

export interface StorageEstimate {
  /** Bytes used (browser estimate). */
  used: number;
  /** Bytes granted (browser estimate; 0 if unknown). */
  quota: number;
  /** `used / quota` in 0–1 (0 when quota is unknown). */
  percent: number;
}

/** Best-effort storage estimate from the StorageManager API. */
export async function getStorageEstimate(): Promise<StorageEstimate> {
  if (typeof navigator === "undefined" || navigator.storage?.estimate === undefined) {
    return { used: 0, quota: 0, percent: 0 };
  }
  const { usage = 0, quota = 0 } = await navigator.storage.estimate();
  return { used: usage, quota, percent: quota > 0 ? usage / quota : 0 };
}

export interface StorageBreakdown {
  profiles: number;
  snapshots: number;
  quotes: number;
  auditLog: number;
  config: number;
  /** Estimated remainder (browser usage minus the categories above). */
  other: number;
}

function jsonBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

/**
 * Approximate per-category storage use. Object sizes are estimated from their
 * JSON encoding; snapshot payloads add the exact compressed byte length.
 */
export async function getStorageBreakdown(): Promise<StorageBreakdown> {
  const [profiles, snapshots, quotes, auditLog] = await Promise.all([
    db.profiles.toArray(),
    db.snapshots.toArray(),
    db.quotes.toArray(),
    db.auditLog.toArray(),
  ]);

  const profilesBytes = profiles.reduce((sum, p) => sum + jsonBytes(p), 0);
  const snapshotsBytes = snapshots.reduce(
    (sum, s) => sum + s.rows_compressed.byteLength + jsonBytes({ ...s, rows_compressed: null }),
    0,
  );
  const quotesBytes = quotes.reduce((sum, q) => sum + jsonBytes(q), 0);
  const auditBytes = auditLog.reduce((sum, e) => sum + jsonBytes(e), 0);

  let configBytes = 0;
  try {
    configBytes = localStorage.getItem("catalogkit.config")?.length ?? 0;
  } catch {
    configBytes = 0;
  }

  const accounted = profilesBytes + snapshotsBytes + quotesBytes + auditBytes + configBytes;
  const { used } = await getStorageEstimate();
  const other = Math.max(0, used - accounted);

  return {
    profiles: profilesBytes,
    snapshots: snapshotsBytes,
    quotes: quotesBytes,
    auditLog: auditBytes,
    config: configBytes,
    other,
  };
}

/** Bytes stored for one profile (compressed snapshot payloads + their metadata). */
export async function getProfileStorageBytes(profileId: string): Promise<number> {
  const snapshots = await db.snapshots.where("profile_id").equals(profileId).toArray();
  return snapshots.reduce(
    (sum, s) => sum + s.rows_compressed.byteLength + jsonBytes({ ...s, rows_compressed: null }),
    0,
  );
}

/** Bytes stored per profile, keyed by profile id — one pass over all snapshots. */
export async function getStorageByProfile(): Promise<Record<string, number>> {
  const snapshots = await db.snapshots.toArray();
  const byProfile: Record<string, number> = {};
  for (const s of snapshots) {
    const bytes = s.rows_compressed.byteLength + jsonBytes({ ...s, rows_compressed: null });
    byProfile[s.profile_id] = (byProfile[s.profile_id] ?? 0) + bytes;
  }
  return byProfile;
}

/** Recommended response when a write would push storage past a threshold. */
export type QuotaAction = "proceed" | "warn" | "free_space";

export interface QuotaCheckResult {
  /** `false` when the write is projected to exceed the quota entirely. */
  allowed: boolean;
  level: WarningLevel | "exceeded";
  action: QuotaAction;
  /** Projected fill fraction after the write (0–1). */
  projectedPercent: number;
  message: string;
}

/**
 * Assess whether an upcoming write of `estimatedBytes` is safe.
 *
 * Returns an action recommendation: `proceed` (under warning), `warn`
 * (warning/critical band), or `free_space` (would exceed quota — the UI should
 * offer auto-prune, export-then-delete, or cancel).
 */
export async function checkQuotaBeforeWrite(estimatedBytes: number): Promise<QuotaCheckResult> {
  const { used, quota } = await getStorageEstimate();

  if (quota <= 0) {
    return {
      allowed: true,
      level: "ok",
      action: "proceed",
      projectedPercent: 0,
      message: "Storage estimate unavailable; proceeding.",
    };
  }

  const projectedPercent = (used + estimatedBytes) / quota;

  if (projectedPercent >= 1) {
    return {
      allowed: false,
      level: "exceeded",
      action: "free_space",
      projectedPercent,
      message: "This upload would exceed available storage. Free space or export first.",
    };
  }

  const level = warningLevelFor(projectedPercent);
  const pct = Math.round(projectedPercent * 100);
  if (level === "critical" || level === "warning") {
    return {
      allowed: true,
      level,
      action: "warn",
      projectedPercent,
      message: `Storage would reach ${pct}% after this write.`,
    };
  }
  return {
    allowed: true,
    level,
    action: "proceed",
    projectedPercent,
    message: `Storage would be ${pct}% after this write.`,
  };
}
